import { defineStore } from 'pinia';
import api from '@/services/api';

/**
 * PendingSuppliers store - gerencia todos os fornecedores pendentes
 */
export const usePendingSuppliersStore = defineStore('pendingSuppliers', {
  // State
  state: () => ({
    pendingSuppliers: [],
    currentPendingSupplier: null,
    isLoading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 12,
      total: 0,
      totalPages: 0,
    },
  }),

  // Getters
  getters: {
    // Obter um fornecedor pendente específico por ID
    getPendingSupplierById: (state) => (id) => {
      return state.pendingSuppliers.find((supplier) => supplier.id === id);
    },
  },

  // Actions
  actions: {
    // Buscar todos os fornecedores pendentes
    async fetchPendingSuppliers(params = {}) {
      this.isLoading = true;
      try {
        const defaultParams = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          status: 'PENDING', // Por padrão, buscar apenas pendentes
        };

        const response = await api.pendingSuppliers.getAll({
          ...defaultParams,
          ...params,
        });

        if (response.data && response.data.success) {
          this.pendingSuppliers = response.data.data.data;

          // Atualizar informações de paginação
          if (response.data.data.meta) {
            const meta = response.data.data.meta;
            this.pagination = {
              page: meta.currentPage || 1,
              limit: meta.perPage || 12,
              total: meta.total || 0,
              totalPages: meta.lastPage || 1,
            };
          }

          this.error = null;
        } else {
          throw new Error('Formato de resposta inválido');
        }
      } catch (err) {
        console.error('Erro ao buscar fornecedores pendentes:', err);
        this.error = err.message || 'Falha ao buscar fornecedores pendentes';
      } finally {
        this.isLoading = false;
      }
    },

    // Buscar detalhes de um fornecedor pendente específico
    async fetchPendingSupplierById(id) {
      if (!id) return null;

      this.isLoading = true;
      try {
        const response = await api.pendingSuppliers.getById(id);

        if (response.data && response.data.success) {
          this.currentPendingSupplier = response.data.data.data;
          this.error = null;
          return this.currentPendingSupplier;
        } else {
          throw new Error('Formato de resposta inválido');
        }
      } catch (err) {
        console.error(`Erro ao buscar fornecedor pendente ${id}:`, err);
        this.error =
          err.message || 'Falha ao buscar detalhes do fornecedor pendente';
        return null;
      } finally {
        this.isLoading = false;
      }
    },

    // Aprovar um fornecedor pendente
    async approvePendingSupplier(id, data) {
      this.isLoading = true;
      try {
        const response = await api.pendingSuppliers.approve(id, data);

        if (response.data && response.data.success) {
          // Remover o fornecedor da lista de pendentes após aprovação
          this.pendingSuppliers = this.pendingSuppliers.filter(
            (supplier) => supplier.id !== id
          );

          // Atualizar total
          this.pagination.total = Math.max(0, this.pagination.total - 1);

          this.error = null;
          return response.data;
        } else {
          throw new Error('Formato de resposta inválido');
        }
      } catch (err) {
        console.error(`Erro ao aprovar fornecedor pendente ${id}:`, err);
        this.error = err.message || 'Falha ao aprovar fornecedor pendente';
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // Rejeitar um fornecedor pendente
    async rejectPendingSupplier(id, data) {
      this.isLoading = true;
      try {
        const response = await api.pendingSuppliers.reject(id, data);

        if (response.data && response.data.success) {
          // Remover o fornecedor da lista de pendentes após rejeição
          this.pendingSuppliers = this.pendingSuppliers.filter(
            (supplier) => supplier.id !== id
          );

          // Atualizar total
          this.pagination.total = Math.max(0, this.pagination.total - 1);

          this.error = null;
          return response.data;
        } else {
          throw new Error('Formato de resposta inválido');
        }
      } catch (err) {
        console.error(`Erro ao rejeitar fornecedor pendente ${id}:`, err);
        this.error = err.message || 'Falha ao rejeitar fornecedor pendente';
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // Atualizar paginação
    updatePagination(pagination) {
      this.pagination = {
        ...this.pagination,
        ...pagination,
      };
    },

    // Resetar store
    resetStore() {
      this.pendingSuppliers = [];
      this.currentPendingSupplier = null;
      this.error = null;
      this.pagination = {
        page: 1,
        limit: 12,
        total: 0,
        totalPages: 0,
      };
    },
  },
});
